import { useLoaderData, useRevalidator, useRouteError } from "react-router-dom";
import GenericErrorDisplay, { useSetError } from "../generic-error";
import { useState } from "react";
import SearchBox from "../../components/inputs/search-box";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faClose,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { IMAGE_HOST } from "../../server/config";
import TextInput from "../../components/inputs/text";
import { CircularProgress } from "@mui/material";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import TransmedAdmin from "../../dtos/transmed/process/admin";
import OutputTransmedEdition from "../../dtos/transmed/process/edition";
import TransmedProcessService from "../../service/transmed/process";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import CheckboxInput from "../../components/inputs/checkbox";
import TransmedProofService from "../../service/transmed/process/proof";
import {
  PublicTransmedRegistration,
  TransmedRegistrationStage,
} from "../../dtos/transmed/process/registration/registration";
import TransmedRegistrationPaymentService from "../../service/transmed/process/registration/payment";

export const path = "transmed/proof";

type LoaderType = {
  admin: TransmedAdmin;
  registrations: PublicTransmedRegistration[];
  edition: OutputTransmedEdition;
};

export async function loader(): Promise<LoaderType> {
  const admin = new TransmedProcessService().read.admin();
  const registrations =
    new TransmedRegistrationPaymentService().read.incomings();
  const edition = new TransmedProcessService().read.edition();

  return {
    admin: await admin,
    registrations: await registrations,
    edition: await edition,
  };
}

const pagination = {
  clickable: true,
  renderBullet: function (index: number, className: string) {
    return '<span class="' + className + '">' + (index + 1) + "</span>";
  },
};

export function Component() {
  const service = new TransmedProofService();
  const { admin, registrations, edition } = useLoaderData() as LoaderType;

  const [userId, setUserId] = useState<number | null>(null);
  const [answer, setAnswer] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const revalidator = useRevalidator();
  const setError = useSetError();

  const [search, setSearch] = useState<string>("");

  const { width } = useWindowDimensions();

  return (
    <>
      <h1>Validarea participării</h1>
      <SearchBox
        onSearch={(search) => setSearch(search)}
        placeholder="Caută participanți..."
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--gap-small)",
        }}
      >
        {registrations
          .filter(
            (registration) =>
              registration.stage === TransmedRegistrationStage.accepted &&
              registration.participation_proof_1 !== null &&
              registration.participation_proof_2 !== null &&
              registration.participation_proof_3 !== null &&
              registration.participation_proof_4 !== null &&
              registration.participation_proof_5 !== null &&
              registration.certificate_text !== null &&
              (search.length === 0
                ? true
                : (
                    registration.user.first_name +
                    " " +
                    registration.user.last_name +
                    " " +
                    registration.user.public_id
                  ).includes(search.trim()))
          )
          .map((registration: PublicTransmedRegistration) => (
            <div
              className="surface"
              key={registration.id}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--gap-small)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "var(--gap-small)",
                }}
              >
                <h3
                  style={{
                    wordBreak: "break-word",
                    textAlign: "left",
                  }}
                >
                  {registration.user.first_name +
                    " " +
                    registration.user.last_name}
                </h3>
                <div className="stretcher" />
                <span
                  style={{
                    backgroundColor: registration.participation_proof_validator
                      ? registration.participation_proof_message
                        ? new Date() < edition.participation_proof_deadline
                          ? "var(--color-dark)"
                          : "red"
                        : "green"
                      : "yellow",
                    color: registration.participation_proof_validator
                      ? "white"
                      : "var(--color-dark)",
                    padding: "var(--gap-xs) var(--gap-small)",
                    borderRadius: 1000,
                  }}
                  className="label"
                >
                  {registration.participation_proof_validator
                    ? registration.participation_proof_message
                      ? new Date() < edition.participation_proof_deadline
                        ? "Returnat"
                        : "Respins"
                      : "Validat"
                    : "Nevalidat"}
                </span>
                <button
                  className="primary button text"
                  onClick={() => {
                    setUserId(
                      registration.id === userId ? null : registration.id
                    );
                    setAnswer(registration.participation_proof_message ?? "");
                  }}
                >
                  <FontAwesomeIcon
                    icon={
                      registration.id === userId ? faChevronUp : faChevronDown
                    }
                  />
                </button>
              </div>
              {registration.id === userId && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: width > 1150 ? "row" : "column-reverse",
                    alignItems: width > 1150 ? "stretch" : "center",
                    gap: "var(--gap-small)",
                  }}
                >
                  <form
                    style={{
                      flex: 1,
                      alignSelf: "stretch",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      gap: "var(--gap-small)",
                    }}
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setError("");
                      setLoading(true);
                      try {
                        await service.update.accept(registration.id);
                      } catch (error) {
                        setError(error);
                      } finally {
                        revalidator.revalidate();
                        setLoading(false);
                      }
                    }}
                  >
                    <CheckboxInput
                      label="Toate adeverințele sunt conforme"
                      required
                    />
                    <CheckboxInput
                      label="Studentul a participat la activitățile științifice necesare"
                      required
                    />
                    <CheckboxInput
                      label="Studentul a participat la activitățile sociale necesare"
                      required
                    />
                    <CheckboxInput
                      label="Studentul a respectat codul de conduită"
                      required
                    />
                    <div className="surface">
                      <b>Text pe diplomă</b>
                      <div style={{ margin: "var(--gap-medium)" }}>
                        {registration.certificate_text}
                      </div>
                      <CheckboxInput
                        label="Textul de trecut pe diplomă este conform"
                        required
                      />
                    </div>
                    <div className="stretcher" />
                    <TextInput
                      multiline
                      rows={3}
                      label={
                        new Date() < edition.participation_proof_deadline
                          ? "Mesaj returnare"
                          : "Mesaj respingere"
                      }
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: "var(--gap-small)",
                      }}
                    >
                      {admin.validate && (
                        <>
                          <button
                            type="submit"
                            className="primary button"
                            style={{ margin: 0, ...{ "--color": "green" } }}
                            disabled={
                              (registration.participation_proof_validator !==
                                null &&
                                registration.participation_proof_message ===
                                  null) ||
                              answer.length > 0
                            }
                          >
                            <FontAwesomeIcon icon={faCheck} /> Validează
                          </button>
                          <button
                            disabled={
                              (registration.participation_proof_validator !==
                                null &&
                                registration.participation_proof_message !==
                                  null) ||
                              answer.length === 0
                            }
                            className="secondary button"
                            style={{
                              margin: 0,
                              ...(new Date() <
                              edition.participation_proof_deadline
                                ? {}
                                : { "--color": "red" }),
                            }}
                            type="button"
                            onClick={async () => {
                              setError("");
                              setLoading(true);
                              try {
                                if (answer.length === 0) {
                                  throw new Error(
                                    "Mesajul de returnare nu poate fi gol."
                                  );
                                }

                                await service.update.reject(
                                  registration.id,
                                  answer
                                );
                              } catch (error) {
                                setError(error);
                              } finally {
                                revalidator.revalidate();
                                setLoading(false);
                              }
                            }}
                          >
                            <FontAwesomeIcon
                              icon={
                                new Date() <
                                edition.participation_proof_deadline
                                  ? faUndo
                                  : faClose
                              }
                            />{" "}
                            {new Date() < edition.participation_proof_deadline
                              ? "Returnează"
                              : "Respinge"}
                          </button>
                        </>
                      )}
                      {(loading || revalidator.state === "loading") && (
                        <CircularProgress />
                      )}
                      <div className="stretcher" />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          gap: "var(--gap-xs)",
                          alignSelf: "stretch",
                        }}
                      >
                        <div className="label">Ultimul validator:</div>
                        <div>
                          {registration.participation_proof_validator
                            ?.public_id ?? <i>(nevalidat)</i>}
                        </div>
                      </div>
                    </div>
                  </form>
                  <div
                    style={{
                      width:
                        "min(500px, calc(100dvw - 8 * var(--surface-padding)))",
                    }}
                    className="surface"
                  >
                    <Swiper
                      pagination={pagination}
                      navigation={true}
                      modules={[Pagination, Navigation]}
                      className="transmed-proof-swiper"
                    >
                      {[
                        registration.participation_proof_1,
                        registration.participation_proof_2,
                        registration.participation_proof_3,
                        registration.participation_proof_4,
                        registration.participation_proof_5,
                      ].map((proof, index) => (
                        <SwiperSlide key={index}>
                          <img src={IMAGE_HOST + proof} alt="Inexistent" />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                </div>
              )}
            </div>
          )) ?? <div className="label">Nu există participanți.</div>}
      </div>
    </>
  );
}
Component.displayName = "page-account-transmed-proof";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-account-transmed-proof-error";
